import { ComponentProps, ComponentType, FC, ReactNode } from 'react'
import { QueryClient, QueryClientProvider as BaseQueryClientProvider } from 'react-query'
import { fireEvent } from 'utilities/analytics'
import type { ResponseError } from 'api'

interface Props {
  children: ReactNode
}

export const QueryClientProvider: FC<Props> = ({ children }) => {
  return <BaseQueryClientProvider client={queryClientProvider}>{children}</BaseQueryClientProvider>
}

export function withQueryClient<C extends ComponentType<any>>(Component: C): FC<ComponentProps<C>> {
  return (props) => {
    return (
      <QueryClientProvider>
        <Component {...props} />
      </QueryClientProvider>
    )
  }
}

const queryClientProvider = new QueryClient({
  defaultOptions: {
    mutations: {
      onError: (error) => {
        fireEvent('mutation_error', getEventFromError(error))
      },
    },
    queries: {
      onError: (error: ResponseError) => {
        fireEvent('query_error', getEventFromError(error))
      },
      // By default, if any request returns a 401, 422, 403 or 404, don't retry.
      // Any other status code will retry 2 more times.
      retry: (failureCount, error: ResponseError) => {
        const response = error.response
        const status = response?.status ?? 400

        if ([401, 422, 403, 404].includes(status)) {
          return false
        }

        return failureCount < 2
      },
    },
  },
})

function getEventFromError(error: unknown) {
  const response =
    error && typeof error === 'object' && 'response' in error
      ? (error as ResponseError).response
      : null
  const [url] = response
    ? ('error' in response ? response.error : response).url.split('?') ?? []
    : []

  return { status: response?.status, url }
}
